/**
 * THE SETTINGS PAGES, IN ONE PLACE.
 *
 * The sidebar lists them and the settings page renders them. Two copies of this
 * list drift the day somebody adds a tab: it appears on one surface and not the
 * other, which is the same failure as a page with a route and no sidebar row.
 *
 * Only the NAV facts live here - value, label, icon, and who the tab is for.
 * What each tab renders (its component, or its own sub-sections) stays in
 * settings-page, which is the only file that should know.
 */
import {
  Crown, Radio, Route, Settings as Sliders, ShieldCheck, UserRound, Users,
} from 'lucide-react';

export const SETTINGS_TABS = [
  // Yours, not the workspace's, so it sits above the workspace settings.
  { value: 'account', label: 'Your account', icon: UserRound, desc: 'Your name, sign-in and password' },
  { value: 'general', label: 'General', icon: Sliders, desc: 'The organisation, defaults and workspace type' },
  { value: 'routing', label: 'Lead routing', icon: Route, desc: 'Who gets each new lead' },
  // Its own permission module (/team), not /settings - a workspace can let
  // somebody manage people without handing them the integration keys.
  { value: 'team', label: 'Team', icon: Users, gate: '/team', desc: 'People, roles and what each can open' },
  // `sections`: the names only, in the page's own step order, so other pages
  // can say "Settings > Tracking > Install the code". What each renders stays in settings-page (a test holds the
  // two lists to the same values and labels).
  { value: 'tracking', label: 'Tracking', icon: Radio, desc: 'Your sites, install code and where signals go',
    sections: [
      { value: 'goal', label: 'Your goal' },
      { value: 'sites', label: 'Your store' },
      { value: 'orders', label: 'How orders reach us' },
      { value: 'serving', label: 'Choose your address' },
      { value: 'connect', label: 'Connect it' },
      { value: 'install', label: 'Install the code' },
      { value: 'cookies', label: 'Keep cookies alive' },
      { value: 'tagging', label: 'Tagging convention' },
      { value: 'destinations', label: 'Where signals go' },
    ] },
  // DPDP mode, the consent notice, the audit export and a person's rights
  // (components/settings/privacy-consent; the API's services/consent decides).
  { value: 'privacy', label: 'Privacy and consent', icon: ShieldCheck, desc: 'DPDP mode, consent records and a person\u2019s data' },
  { value: 'billing', label: 'Billing', icon: Crown, desc: 'This month\u2019s usage, your plan and invoices' },
];

/*
 * Tabs that became Integrations cards (26 Sep 2026). Each connector's drawer
 * is now its only home; Settings keeps what is yours and the workspace's own
 * setup. An old address opens the card (`open`) or its section (`cat`), and
 * carries any OAuth status the provider sent back, so a bookmark, an email
 * link or a half-finished Google/Zoom/Microsoft/Facebook sign-in still lands
 * on the right card.
 */
export const MOVED_TO_INTEGRATIONS = {
  calendar: { open: 'google_calendar' },
  calendly: { open: 'calendly' },
  calling: { cat: 'calling' },
  email: { open: 'email_sending' },
  esp: { cat: 'email' },
  leadsquared: { open: 'leadsquared' },
  hubspot: { open: 'hubspot' },
  verification: { open: 'otp_sms' },
  payments: { cat: 'payments' },
  leadads: { open: 'meta_lead_ads' },
  webhooks: { open: 'outbound_webhooks' },
  api: { open: 'partner_api' },
};

// Tabs folded into another tab (26 Sep 2026). Store held the purchase code
// (now in Tracking > Install code, for a custom store) and Shopify's order
// webhook (now Integrations > Shopify > Connection).
export const MOVED_WITHIN_SETTINGS = {
  store: { tab: 'tracking', sub: 'install' },
};
const CARRY = ['fb', 'n', 'msg', 'zoom', 'ms', 'reason', 'gdm', 'connected', 'error'];

// Every remaining tab applies to both kinds of workspace. The lead-only tabs
// this used to hide (Calendar,
// Calendly, Lead Ads, LeadSquared) are Integrations cards now, which Integrations
// filters by workspace kind itself.
export const ECOM_HIDE_TABS = new Set([]);

// The tab is a QUERY on one page, not a path of its own - which is why the rail
// highlights it through queryChildActive rather than by pathname.
// `sub` opens one section of a tab that has them (Tracking).
export const settingsPath = (value, sub) => `/settings?tab=${value}${sub ? `&sub=${sub}` : ''}`;

export const settingsTabsFor = (ecomOn) => (ecomOn
  ? SETTINGS_TABS.filter((t) => !ECOM_HIDE_TABS.has(t.value))
  : SETTINGS_TABS.filter((t) => !t.ecomOnly));

/*
 * Links written while Settings was briefly regrouped (25 Sep 2026, reverted the
 * same day) keep landing in the right place. That version addressed a section
 * as `section=` and knew a few shortcut tabs; guides, emails and the knowledge
 * base carried those links out, so they are read here, forever, the way the
 * old page's own `sub=` is.
 */
// The WhatsApp page: every WhatsApp setting (connection, templates, automatic
// replies, product catalog, website widget, usage and cost), under
// Integrations and in the sidebar's Messaging group. It was a Settings tab
// until 26 Sep 2026.
export const WHATSAPP_PAGE = '/integrations/whatsapp';
export const WHATSAPP_SECTIONS = [
  { value: 'connection', label: 'Connection' },
  { value: 'templates', label: 'Templates' },
  { value: 'replies', label: 'Automatic replies' },
  { value: 'catalog', label: 'Product catalog' },
  { value: 'widget', label: 'Website widget' },
  { value: 'usage', label: 'Usage & cost' },
];
export const whatsappPath = (sub) => `${WHATSAPP_PAGE}${sub ? `?sub=${sub}` : ''}`;

// The Shopify page (Integrations > Shopify): the order webhook, the install
// code, the checkout the store runs, and what arrived. The checkout apps'
// cards open its Checkout section with the app in focus (&partner=).
export const SHOPIFY_PAGE = '/integrations/shopify';
// In set-up order: orders first, then which checkout the store runs, then the
// custom pixel built for it. The site script is not here - it is Settings >
// Tracking > Install code, the same for every site.
export const SHOPIFY_SECTIONS = [
  { value: 'connection', label: 'Connection' },
  // Orders the webhooks missed, read back from Shopify's API every hour
  // (owner, 12 Oct 2026: on the Shopify page, easy to find and remember).
  { value: 'catchup', label: 'Order catch-up' },
  { value: 'checkout', label: 'Checkout' },
  { value: 'pixel', label: 'Custom pixel' },
  { value: 'health', label: 'Health' },
];

export const TAB_ALIASES = {
  goal: { tab: 'tracking', sub: 'goal' },
  sites: { tab: 'tracking', sub: 'sites' },
  install: { tab: 'tracking', sub: 'install' },
  tagging: { tab: 'tracking', sub: 'tagging' },
  serving: { tab: 'tracking', sub: 'serving' },
  destinations: { tab: 'tracking', sub: 'destinations' },
  widget: { tab: 'whatsapp', sub: 'widget' },
  templates: { tab: 'whatsapp', sub: 'templates' },
  whatsapp: { tab: 'whatsapp' },
  // Company details are on Your account here (the regroup had moved them to Billing).
  company: { tab: 'account' },
  departments: { tab: 'team' },
};

/**
 * Which tab (and section) an address means. `section` is read as `sub`; an
 * alias tab resolves to its real tab; anything unknown, or hidden for this
 * workspace, falls back to General.
 */
export function resolveSettingsLink(search, { ecomOn = false } = {}) {
  const sp = new URLSearchParams(String(search || '').replace(/^\?/, ''));
  const raw = sp.get('tab');
  const alias = raw ? TAB_ALIASES[raw] : null;
  const want = alias ? alias.tab : raw;
  // WhatsApp left Settings for its own page (Integrations > WhatsApp). Every
  // old address - the tab, its sections, the widget/templates shortcuts - goes
  // there, on the same section.
  if (want && MOVED_WITHIN_SETTINGS[want]) return MOVED_WITHIN_SETTINGS[want];
  if (want && MOVED_TO_INTEGRATIONS[want]) {
    const out = new URLSearchParams(MOVED_TO_INTEGRATIONS[want]);
    for (const k of CARRY) if (sp.get(k) != null) out.set(k, sp.get(k));
    return { redirect: `/integrations?${out.toString()}` };
  }
  if (!raw && (sp.get('zoom') || sp.get('ms') || sp.get('fb'))) {
    // An OAuth round trip that returned to bare /settings.
    const open = sp.get('zoom') ? 'zoom' : sp.get('ms') ? 'microsoft_calendar' : 'meta_lead_ads';
    const out = new URLSearchParams({ open });
    for (const k of CARRY) if (sp.get(k) != null) out.set(k, sp.get(k));
    return { redirect: `/integrations?${out.toString()}` };
  }
  if (want === 'whatsapp') {
    return { redirect: whatsappPath(alias?.sub || sp.get('sub') || sp.get('section') || null) };
  }
  const allowed = settingsTabsFor(ecomOn).map((t) => t.value);
  const tab = allowed.includes(want) ? want : 'general';
  const sub = alias?.sub || sp.get('sub') || sp.get('section') || null;
  return { tab, sub: tab === want ? sub : null };
}
